// ⚠️ #1522 — the console.warn calls in this file stay warn, deliberately.
//
// main.tsx runs captureConsoleIntegration at levels ["error"], so a warn
// produces no Sentry event. That is the point here: attribution capture
// fails on a timer or a shim, nothing she did depends on it, and nothing is
// waiting on the result. The repo's rule is "warn means expected and handled,
// error means a human should see it" — these are the warns that rule exists
// for, and converting them is the quota flood main.tsx's comment warns about.
//
// A failed WRITE she initiated does not belong here. If one is added to this
// file, report it with reportError.

// Lightweight signup attribution capture.
//
// Reads UTM params from the URL on app mount, records HOW the person arrived
// (referrer, landing URL, platform) and stashes it in localStorage so it
// survives OAuth redirects (Google/Apple) AND the email-confirmation hop into
// a brand new tab. After a NEW signup completes, `writeAttributionIfNew`
// flushes it to the signup_attribution table.
//
// Issue #351 — the table sat at exactly 0 rows for ~8 weeks. Two reasons, both
// fixed here:
//   Gate A: capture used to bail out entirely when the URL had no utm_* param,
//     so an untagged (organic) arrival stashed nothing and wrote nothing. But
//     daily-digest reads a row with a null utm_source as the "organic/untagged"
//     bucket — a shape the writer could never produce. We now ALWAYS stash, so
//     every signup gets exactly one row and organic becomes a real number
//     instead of an absence.
//   Gate B: the stash lived in sessionStorage, which is per-tab. With
//     "Confirm email" ON in Supabase Auth, the mama clicks the link in her mail
//     app, which opens a NEW tab at /auth/callback — empty sessionStorage, so
//     every email signup lost its attribution by construction. localStorage is
//     per-origin and survives that hop.
//
// Still deliberately out of scope (Gate C): App Store `ct=` campaign tokens and
// Play Install Referrer never reach a web URL, so native installs land here as
// organic. That needs Median-side config and is its own project.

import { reportError } from "./reportError";
import { supabase } from "@/integrations/supabase/client";
// #355 — this file carried its own copy of detectPlatform() with the same
// Median-means-iOS bug as analytics.ts. The copy was dormant (no rows were
// being written at all) but would have gone live the moment #351 was fixed.
// Both now share one implementation so the bug can't survive in a second file.
import { detectPlatform } from "@/lib/platform";

const STORAGE_KEY = "kin_utm_attribution";

/**
 * How long a stashed capture stays valid. Short on purpose: this describes a
 * single arrival, not a lifetime attribution window. 24h comfortably covers
 * "land → sign up → confirm the email a few hours later" without letting a
 * week-old landing get credited to an unrelated signup.
 */
export const STASH_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Only accounts created inside this window get an attribution row. Without
 * this, "always stash" (above) would mean any pre-existing user signing in on
 * a fresh device gets a row written TODAY — and daily-digest counts rows by
 * created_at, so those logins would show up as brand new signups.
 */
export const SIGNUP_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Postgres unique-violation. On the `signup_attribution` insert it means "already
 * recorded", which is this function's goal state rather than a failure. #1697.
 */
const DUPLICATE_KEY = "23505";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

/**
 * Query params that may carry a credential. Issue #638: `landing_url` stored
 * `window.location.href` verbatim, and Supabase's email-confirmation hop lands
 * on /auth/callback with a full access_token AND refresh_token in the URL
 * FRAGMENT. Both were written to the database in plaintext, on every signup
 * that confirmed by email.
 *
 * The fragment is dropped wholesale rather than filtered — nothing we want for
 * attribution has ever lived after the "#", and an allow-list there would be
 * one new Supabase param away from leaking again. These named params cover the
 * query-string half (PKCE `code`, magic-link `token`, and the OAuth pair).
 */
const SENSITIVE_PARAMS = [
  "access_token",
  "refresh_token",
  "provider_token",
  "provider_refresh_token",
  "id_token",
  "token",
  "code",
] as const;

/**
 * Strip credentials out of a URL before it is stored. Keeps utm_* and every
 * other ordinary param intact — this is about secrets, not about tidying.
 *
 * Fails CLOSED: anything that can't be parsed and still smells like it holds a
 * token is dropped entirely rather than stored on the theory that it's
 * probably fine. Losing one referrer string costs a row of analytics; guessing
 * wrong costs a live session token.
 */
export const sanitizeUrl = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    url.hash = "";
    for (const key of SENSITIVE_PARAMS) url.searchParams.delete(key);
    return url.toString();
  } catch {
    const withoutFragment = raw.split("#")[0];
    const stillSuspect = SENSITIVE_PARAMS.some((k) => withoutFragment.includes(k));
    return stillSuspect ? null : withoutFragment;
  }
};

/**
 * True when this page load is a return trip from Stripe Checkout rather than an
 * arrival. Issue #639: a returning mom coming back to /pro?checkout=success
 * with a checkout.stripe.com referrer was being logged as a fresh acquisition,
 * so paid conversions counted twice — once as the signup they really were, and
 * again as a brand new "Stripe" signup.
 *
 * ⚠️ Deliberately does NOT treat a bare /pro landing as a checkout return, even
 * though the issue's task list says "/pro or checkout=success". /pro is a
 * perfectly good ad landing page, and skipping it would silently throw away the
 * attribution for any campaign pointed at the pricing page — replacing an
 * over-count with an under-count. The two signals below are unambiguous; the
 * path on its own is not.
 */
export const isCheckoutReturn = (
  landingUrl: string | null,
  referrerUrl: string | null,
): boolean => {
  if (referrerUrl) {
    try {
      if (new URL(referrerUrl).hostname.endsWith("checkout.stripe.com")) return true;
    } catch {
      if (referrerUrl.includes("checkout.stripe.com")) return true;
    }
  }
  if (landingUrl) {
    try {
      if (new URL(landingUrl).searchParams.get("checkout") === "success") return true;
    } catch {
      if (landingUrl.includes("checkout=success")) return true;
    }
  }
  return false;
};

/**
 * Issue #639: 53 rows against 7 real signups, arriving in same-millisecond
 * clusters. The "does a row already exist?" query below was never the problem —
 * it's that check-then-insert is not atomic, and `writeAttributionIfNew` fires
 * on every auth-state-change. Several calls read "no row", then all insert.
 *
 * These two sets close the window WITHIN a tab; the unique constraint added in
 * migration 20260807000000 is the real backstop across tabs and devices. Both
 * are needed — the constraint alone would turn the race into a pile of failed
 * inserts and Sentry noise instead of a pile of rows.
 */
const writeInFlight = new Set<string>();
const writeSettled = new Set<string>();

/** Test-only: module-level guards outlive a single test otherwise. */
export const __resetAttributionGuards = () => {
  writeInFlight.clear();
  writeSettled.clear();
};

interface AttributionPayload {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer_url: string | null;
  landing_url: string | null;
  platform: string | null;
}

interface StashRecord {
  payload: AttributionPayload;
  captured_at: number;
}

const hasAnyUtm = (payload: AttributionPayload): boolean =>
  UTM_KEYS.some((k) => payload[k]);

/** Read the stash, treating anything expired / corrupt / pre-TTL-format as absent. */
const readStash = (now: number): StashRecord | null => {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StashRecord>;
    if (!parsed || typeof parsed.captured_at !== "number" || !parsed.payload) return null;
    if (now - parsed.captured_at > STASH_TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as StashRecord;
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

/**
 * Run once on app mount. Records how this arrival happened — ALWAYS, tagged or
 * not (see Gate A above).
 */
export const captureUtmParamsOnLoad = () => {
  try {
    if (typeof window === "undefined") return;
    const now = Date.now();
    const params = new URLSearchParams(window.location.search);

    // A return trip from Stripe is not an arrival — don't stash it at all, so
    // it can never become the basis of an attribution row (#639).
    if (isCheckoutReturn(window.location.href, document.referrer || null)) return;

    const payload: AttributionPayload = {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_content: params.get("utm_content"),
      utm_term: params.get("utm_term"),
      // #638 — both of these used to be stored raw. The email-confirmation
      // landing carries live Supabase tokens in its fragment.
      referrer_url: sanitizeUrl(document.referrer || null),
      landing_url: sanitizeUrl(window.location.href),
      platform: detectPlatform(),
    };

    // First touch wins (preserve the original landing attribution), with one
    // exception: an untagged stash gets upgraded by a later TAGGED landing in
    // the same window. Otherwise an untagged first hit would permanently
    // shadow the campaign link that actually drove the signup.
    const existing = readStash(now);
    if (existing && (!hasAnyUtm(payload) || hasAnyUtm(existing.payload))) return;

    const record: StashRecord = { payload, captured_at: now };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    console.warn("captureUtmParamsOnLoad failed:", e);
  }
};

/**
 * Decide whether this SIGNED_IN event belongs to a brand new account.
 * Supabase fires SIGNED_IN for logins too, and there's no separate signup
 * event, so account age is the signal. Missing/unparseable created_at is
 * treated as new ON PURPOSE: an over-strict guard here recreates exactly the
 * silent-zero failure this issue is about, and the "row already exists" check
 * downstream caps the damage at one row per user.
 */
const isFreshSignup = (userCreatedAt: string | null | undefined, now: number): boolean => {
  if (!userCreatedAt) {
    console.warn("writeAttributionIfNew: no user created_at, treating as a new signup");
    return true;
  }
  const created = new Date(userCreatedAt).getTime();
  if (Number.isNaN(created)) {
    console.warn("writeAttributionIfNew: unparseable created_at, treating as a new signup");
    return true;
  }
  return now - created <= SIGNUP_WINDOW_MS;
};

/** After a new signup completes, write the attribution row (once) and clear storage. */
export const writeAttributionIfNew = async (
  userId: string,
  userCreatedAt?: string | null,
) => {
  try {
    if (typeof window === "undefined") return;
    const now = Date.now();

    // Cheap local check first — an ordinary login by an established user gets
    // no row and costs no query.
    if (!isFreshSignup(userCreatedAt, now)) return;

    // #639 — this fires on every auth-state-change. Anything already decided
    // for this user, or already mid-flight, must not race a second insert.
    // Deliberately NOT set on the error path below: a failed write should still
    // be retryable on the next auth event, which is why the stash survives it.
    if (writeSettled.has(userId) || writeInFlight.has(userId)) return;

    const stash = readStash(now);
    if (!stash) return;

    // A stash captured on a checkout return isn't an acquisition. Capture
    // already refuses to create one, so this only catches a stash written
    // before this code shipped.
    if (isCheckoutReturn(stash.payload.landing_url, stash.payload.referrer_url)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }

    writeInFlight.add(userId);
    try {
      // Skip if a row already exists for this user
      const { data: existing } = await supabase
        .from("signup_attribution")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();
      if (existing) {
        writeSettled.add(userId);
        window.localStorage.removeItem(STORAGE_KEY);
        return;
      }

      const { error } = await supabase.from("signup_attribution").insert({
        user_id: userId,
        ...stash.payload,
      });
      if (error) {
        const code = (error as { code?: string }).code;

        // ── 23505 IS SUCCESS HERE. #1697. ──────────────────────────────────
        //
        // `signup_attribution_user_id_key` is a unique constraint on user_id, so
        // 23505 on THIS insert means a row for this user already exists — which is
        // precisely the state this function is trying to reach. Verified 2026-09-23
        // that zero users have duplicate rows: the constraint is working and the
        // data is fine. Only the alerting was wrong.
        //
        // ⚠️ It is reached despite the `existing` pre-check above, and that is the
        // interesting part rather than a contradiction: the check and the insert are
        // two round trips, so anything that lands a row between them produces this —
        // a second tab, a re-render racing the first write, or the pre-check's own
        // read failing (its error is deliberately ignored, so a failed SELECT yields
        // `existing === undefined` and falls through to the insert).
        //
        // ⚠️ TREATING IT AS SUCCESS MEANS CLEARING THE STASH, not just skipping the
        // Sentry call. The stash is what makes this run again — it is re-read on
        // every load until it is removed or its 24h TTL expires. So a branch that
        // only silenced Sentry would leave the write retrying, and one duplicate
        // would still produce a fresh event on every page load once alerting came
        // back. Both lines below mirror the `existing` branch exactly.
        if (code === DUPLICATE_KEY) {
          // Visible when debugging locally, silent in production, no Sentry event
          // (captureConsoleIntegration only synthesises events from console.error).
          console.debug("[attribution] row already existed (23505) — treating as recorded", { userId });
          writeSettled.add(userId);
          window.localStorage.removeItem(STORAGE_KEY);
          return;
        }

        // Never console-only: an 8-week silent outage is what got us here.
        // #639's wrapping (a PostgrestError is not an Error, and Sentry titles
        // those "Object captured as exception with keys: …") now lives in
        // reportError, along with #1255's scope-before-log ordering.
        reportError("signup_attribution insert failed", error, {
          tags: {
            area: "signup-attribution",
            writer: "writeAttributionIfNew",
            code: code ?? "no-code",
          },
          extra: { user_id: userId, utm_source: stash.payload.utm_source },
        });
        return;
      }
      writeSettled.add(userId);
      window.localStorage.removeItem(STORAGE_KEY);
    } finally {
      writeInFlight.delete(userId);
    }
  } catch (e) {
    reportError("writeAttributionIfNew failed", e, {
      tags: { area: "signup-attribution", writer: "writeAttributionIfNew" },
      extra: { user_id: userId },
    });
  }
};
